import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { adminAuthenticatedFetch } from '../utils/adminAuthenticatedFetch';
import { apiService } from './apiService';

const API_BASE = featureFlags.general.apiUrl;
const ADMIN_BASE = `${API_BASE}/api/admin/creator-chat`;
const PUBLIC_BASE = `${API_BASE}/api/public/creator-chat`;

function getAdminToken(): string {
  return store.getState().auth.token || '';
}

function adminFetch(url: string, init: RequestInit = {}, timeoutMs = 20000): Promise<Response> {
  return adminAuthenticatedFetch(url, init, timeoutMs);
}

export type CreatorChatPlatform = 'FANVUE' | 'ONLYFANS' | 'BOTH';
export type CreatorChatLauncher = 'BOTTOM_RIGHT' | 'BOTTOM_LEFT';

export interface CreatorChatModal {
  id: number;
  publicId: string;
  name: string;
  title?: string | null;
  subtitle?: string | null;
  welcomeMessage?: string | null;
  systemPrompt?: string | null;
  /** How she types — casing, emoji, abbreviations — as opposed to what she says. */
  writingStyle?: string | null;
  /** IANA zone she supposedly lives in, e.g. Europe/Lisbon. */
  personaTimezone?: string | null;
  /** Hour she is normally up from, 0-23. Null means awake around the clock. */
  awakeFromHour?: number | null;
  awakeToHour?: number | null;
  fallbackMessage?: string | null;
  ctaLabel?: string | null;
  accentColor?: string | null;
  avatarImageUrl?: string | null;
  targetPlatform?: CreatorChatPlatform | string | null;
  launcherPosition?: CreatorChatLauncher | string | null;
  creatorLabel?: string | null;
  published: boolean;
  actionsEnabled?: boolean;
  idleVideoUrl?: string | null;
  voiceEnabled?: boolean;
  ttsVoice?: string | null;
  ttsSpeed?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export type CreatorChatModalPayload = Partial<Omit<CreatorChatModal, 'id' | 'publicId' | 'createdAt' | 'updatedAt'>> & {
  name: string;
};

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatActionPayload {
  slug: string;
  label?: string | null;
  videoUrl: string;
}

export interface ChatVideoJobPayload {
  id: number;
  status: string;
  slug?: string | null;
  label?: string | null;
}

export interface ChatReplyBubble {
  text: string;
  /** Pause before showing this bubble, mimicking the time taken to type it. */
  delayMs: number;
}

export interface ChatResponse {
  reply: string;
  /**
   * The reply split into the separate messages a person would send. Absent on older
   * responses, in which case `reply` is shown as one bubble.
   */
  bubbles?: ChatReplyBubble[] | null;
  /** TTS audio for the reply when voice is enabled. */
  audioUrl?: string | null;
  ttsSkipped?: string | null;
  action?: ChatActionPayload | null;
  videoJob?: ChatVideoJobPayload | null;
  /** Present when realtime enqueue was attempted but failed. */
  videoJobError?: string | null;
  /** Present when realtime was skipped (disabled, no avatar, fal not ready, etc.). */
  videoGenSkipped?: string | null;
  error?: string;
  llmFailed?: boolean;
  degraded?: boolean;
  /** Learned fan display name (when memory is enabled). */
  fanName?: string | null;
}

export interface CreatorChatMemoryResponse {
  history: ChatTurn[];
  fanName?: string | null;
  memoryEnabled?: boolean;
}

export interface PublicVideoJobStatus {
  id: number;
  status: string;
  slug?: string | null;
  videoUrl?: string | null;
  error?: string | null;
}

export interface CreatorChatActionRow {
  id: number;
  modalId: number;
  actionSlug: string;
  label?: string | null;
  videoUrl: string;
  synonyms?: string | null;
  sortOrder: number;
  active: boolean;
  /** When true, clip appears on the Test persona trigger wheel. Default true. */
  showOnTriggerWheel?: boolean;
  /** When true, clip plays on center tap of the trigger wheel (one per modal). */
  triggerWheelCenter?: boolean;
}

export interface PublicCreatorChatAction {
  id?: number;
  actionSlug: string;
  label?: string | null;
  videoUrl: string;
  synonyms?: string | null;
  /** When true (default), clip appears on the fan trigger wheel. */
  showOnTriggerWheel?: boolean;
  /** When true, clip plays on center tap of the trigger wheel. */
  triggerWheelCenter?: boolean;
}

export interface PublicCreatorChatConfig {
  publicId: string;
  title?: string | null;
  subtitle?: string | null;
  welcomeMessage?: string | null;
  ctaLabel?: string | null;
  accentColor?: string | null;
  avatarImageUrl?: string | null;
  targetPlatform?: string | null;
  launcherPosition?: string | null;
  creatorLabel?: string | null;
  actionsEnabled?: boolean;
  idleVideoUrl?: string | null;
  voiceEnabled?: boolean;
  ttsVoice?: string | null;
  memoryEnabled?: boolean;
  /**
   * Whether she reads as around right now. Absent unless the persona has a timezone and
   * waking hours configured, since an indicator that always says "Online" is worse than none.
   */
  presence?: { online: boolean; label: string } | null;
  /** Active clips for instant client-side trigger matching / preload. */
  actions?: PublicCreatorChatAction[];
}

export interface LlmHealthStatus {
  enabled: boolean;
  configured: boolean;
  provider: string;
  model: string;
  keySource: string;
  ok: boolean;
  reachable: boolean;
  message?: string | null;
}

export type VideoGenKind = 'idle' | 'kiss' | 'wave' | 'wink' | 'dance' | 'custom';

export interface FalVideoModelOption {
  id: string;
  label: string;
}

export interface VideoGenHealthStatus {
  enabled: boolean;
  configured: boolean;
  provider: string;
  model: string;
  defaultModel?: string;
  models?: FalVideoModelOption[];
  ok: boolean;
  realtimeEnabled?: boolean;
  message?: string | null;
  lumaOk?: boolean;
  luma?: {
    enabled?: boolean;
    configured?: boolean;
    ok?: boolean;
    model?: string;
    defaultMode?: string;
    message?: string | null;
  };
  sogniOk?: boolean;
  sogni?: {
    enabled?: boolean;
    configured?: boolean;
    ok?: boolean;
    model?: string;
    duration?: number;
    tokenType?: string;
    message?: string | null;
  };
}

export type VideoGenProvider = 'fal' | 'sogni';

export type LumaModifyMode =
  | 'adhere_1'
  | 'adhere_2'
  | 'adhere_3'
  | 'flex_1'
  | 'flex_2'
  | 'flex_3'
  | 'reimagine_1'
  | 'reimagine_2'
  | 'reimagine_3';

export interface ModifyVideoRequest {
  sourceVideoUrl: string;
  prompt: string;
  mode?: LumaModifyMode | string;
  firstFrameUrl?: string;
  preferredSlug?: string;
  synonyms?: string;
}

export interface ImageGenHealthStatus {
  enabled: boolean;
  configured: boolean;
  provider: string;
  model: string;
  ok: boolean;
  message?: string | null;
}

/** Structured look fields for photorealistic avatar generation. */
export interface AvatarGenerateRequest {
  sex: string;
  ageLook: string;
  ethnicity: string;
  skinTone?: string;
  faceShape?: string;
  eyeColor: string;
  eyeShape?: string;
  eyebrows?: string;
  hairColor: string;
  hairLength: string;
  hairStyle: string;
  facialHair?: string;
  lips?: string;
  makeup?: string;
  distinctiveMarks?: string;
  expression?: string;
  bodyType: string;
  heightLook?: string;
  bustLook?: string;
  waistHips?: string;
  tattoos?: string;
  outfitType: string;
  top?: string;
  bottom?: string;
  outfitColors?: string;
  accessories?: string;
  framing: string;
  pose?: string;
  background?: string;
  lighting?: string;
  extraDescription?: string;
  avoid?: string;
  numImages?: number;
}

export interface AvatarGenerateResponse {
  prompt: string;
  imageUrls: string[];
  count: number;
  providerRequestId?: string;
}

export interface CreatorChatVideoJobRow {
  id: number;
  modalId: number;
  actionSlug?: string | null;
  provider?: 'fal' | 'luma' | 'sogni' | string | null;
  status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | string;
  prompt: string;
  sourceImageUrl: string;
  sourceVideoUrl?: string | null;
  providerRequestId?: string | null;
  resultVideoUrl?: string | null;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const creatorChatAdminService = {
  llmHealth: async (probe = true): Promise<LlmHealthStatus> => {
    const res = await adminFetch(`${ADMIN_BASE}/llm-health?probe=${probe ? 'true' : 'false'}`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    }, 20000);
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  videoGenHealth: async (): Promise<VideoGenHealthStatus> => {
    const res = await adminFetch(`${ADMIN_BASE}/video-gen-health`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  imageGenHealth: async (): Promise<ImageGenHealthStatus> => {
    const res = await adminFetch(`${ADMIN_BASE}/image-gen-health`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  generateAvatar: async (payload: AvatarGenerateRequest): Promise<AvatarGenerateResponse> => {
    const res = await adminFetch(
      `${ADMIN_BASE}/generate-avatar`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(payload),
      },
      120000
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `${res.status}`);
    }
    return res.json();
  },

  generateVideo: async (
    modalId: number,
    kind: VideoGenKind,
    customRequest?: string,
    provider: VideoGenProvider = 'fal',
    sourceImageUrl?: string,
    falModel?: string
  ): Promise<CreatorChatVideoJobRow> => {
    const body: {
      kind: VideoGenKind;
      customRequest?: string;
      provider?: VideoGenProvider;
      sourceImageUrl?: string;
      falModel?: string;
    } = {
      kind,
      provider,
    };
    if (kind === 'custom' && customRequest) {
      body.customRequest = customRequest;
    }
    if (sourceImageUrl && sourceImageUrl.trim()) {
      body.sourceImageUrl = sourceImageUrl.trim();
    }
    if (provider === 'fal' && falModel && falModel.trim()) {
      body.falModel = falModel.trim();
    }
    const res = await adminFetch(
      `${ADMIN_BASE}/modals/${modalId}/generate-video`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(body),
      },
      30000
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `${res.status}`);
    }
    return res.json();
  },

  /** Extract last frame JPEG from a Ready job (or video URL) for multi-stage chaining. */
  extractLastFrame: async (
    modalId: number,
    opts: { jobId?: number; videoUrl?: string }
  ): Promise<{ url: string; videoUrl?: string }> => {
    const res = await adminFetch(
      `${ADMIN_BASE}/modals/${modalId}/extract-last-frame`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(opts),
      },
      120000
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `${res.status}`);
    }
    return res.json();
  },

  modifyVideo: async (modalId: number, payload: ModifyVideoRequest): Promise<CreatorChatVideoJobRow> => {
    const res = await adminFetch(
      `${ADMIN_BASE}/modals/${modalId}/modify-video`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(payload),
      },
      30000
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `${res.status}`);
    }
    return res.json();
  },

  listVideoJobs: async (modalId: number): Promise<CreatorChatVideoJobRow[]> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/video-jobs`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  deleteVideoJob: async (modalId: number, jobId: number): Promise<void> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/video-jobs/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
  },

  getVideoJob: async (modalId: number, jobId: number): Promise<PublicVideoJobStatus> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/video-jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  list: async (): Promise<CreatorChatModal[]> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  create: async (data: CreatorChatModalPayload): Promise<CreatorChatModal> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  update: async (id: number, data: Partial<CreatorChatModalPayload>): Promise<CreatorChatModal> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  remove: async (id: number): Promise<void> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
  },

  publish: async (id: number): Promise<CreatorChatModal> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${id}/publish`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  unpublish: async (id: number): Promise<CreatorChatModal> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${id}/unpublish`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  uploadAvatar: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await adminFetch(
      `${ADMIN_BASE}/upload-image`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        body: form,
      },
      30000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    const url: string = json?.url ?? json?.data?.url ?? '';
    if (!url) throw new Error('No URL in upload response');
    return url;
  },

  testChat: async (
    id: number,
    message: string,
    history: ChatTurn[],
    systemPromptOverride?: string
  ): Promise<ChatResponse> => {
    const res = await adminFetch(
      `${ADMIN_BASE}/modals/${id}/test-chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({
          message,
          history,
          systemPromptOverride: systemPromptOverride || undefined,
        }),
      },
      45000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  testProactiveNudge: async (
    id: number,
    kind: 'opening' | 'idle',
    history: ChatTurn[],
    systemPromptOverride?: string
  ): Promise<ChatResponse> => {
    const res = await adminFetch(
      `${ADMIN_BASE}/modals/${id}/test-nudge`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({
          kind,
          history,
          systemPromptOverride: systemPromptOverride || undefined,
        }),
      },
      45000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  listActions: async (modalId: number): Promise<CreatorChatActionRow[]> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/actions`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  createAction: async (
    modalId: number,
    data: {
      actionSlug: string;
      label?: string;
      videoUrl: string;
      synonyms?: string;
      sortOrder?: number;
      active?: boolean;
      showOnTriggerWheel?: boolean;
      triggerWheelCenter?: boolean;
    }
  ): Promise<CreatorChatActionRow> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  updateAction: async (
    modalId: number,
    actionId: number,
    data: Partial<{
      actionSlug: string;
      label: string;
      videoUrl: string;
      synonyms: string;
      sortOrder: number;
      active: boolean;
      showOnTriggerWheel: boolean;
      triggerWheelCenter: boolean;
    }>
  ): Promise<CreatorChatActionRow> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/actions/${actionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  deleteAction: async (modalId: number, actionId: number): Promise<void> => {
    const res = await adminFetch(`${ADMIN_BASE}/modals/${modalId}/actions/${actionId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
  },

  uploadActionVideo: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await adminFetch(
      `${ADMIN_BASE}/upload-action-video`,
      { method: 'POST', headers: { Authorization: `Bearer ${getAdminToken()}` }, body: form },
      120000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    const url: string = json?.url ?? '';
    if (!url) throw new Error('No URL in upload response');
    return url;
  },

  uploadIdleVideo: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await adminFetch(
      `${ADMIN_BASE}/upload-idle-video`,
      { method: 'POST', headers: { Authorization: `Bearer ${getAdminToken()}` }, body: form },
      120000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    const url: string = json?.url ?? '';
    if (!url) throw new Error('No URL in upload response');
    return url;
  },
};

export const creatorChatPublicService = {
  getConfig: async (publicId: string): Promise<PublicCreatorChatConfig> => {
    const res = await fetch(`${PUBLIC_BASE}/${encodeURIComponent(publicId)}?_ts=${Date.now()}`);
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  getHistory: async (publicId: string, visitorId: string): Promise<CreatorChatMemoryResponse> => {
    const q = new URLSearchParams({ visitorId, _ts: String(Date.now()) });
    const res = await fetch(
      `${PUBLIC_BASE}/${encodeURIComponent(publicId)}/history?${q.toString()}`
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  chat: async (
    publicId: string,
    message: string,
    history: ChatTurn[],
    visitorId?: string | null
  ): Promise<ChatResponse> => {
    const res = await fetch(`${PUBLIC_BASE}/${encodeURIComponent(publicId)}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, history, visitorId: visitorId || undefined }),
    });
    if (res.status === 429) throw new Error('429');
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  proactiveNudge: async (
    publicId: string,
    kind: 'opening' | 'idle',
    history: ChatTurn[],
    visitorId?: string | null
  ): Promise<ChatResponse> => {
    const res = await fetch(`${PUBLIC_BASE}/${encodeURIComponent(publicId)}/nudge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, history, visitorId: visitorId || undefined }),
    });
    if (res.status === 429) throw new Error('429');
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },

  getVideoJob: async (publicId: string, jobId: number): Promise<PublicVideoJobStatus> => {
    const res = await fetch(
      `${PUBLIC_BASE}/${encodeURIComponent(publicId)}/video-jobs/${jobId}?_ts=${Date.now()}`
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
};

/** Stable anonymous id per fan per modal (localStorage). Used for 30-day chat memory. */
export function getCreatorChatVisitorId(publicId: string): string {
  const key = `creator-chat-visitor-${publicId}`;
  try {
    let id = localStorage.getItem(key);
    if (!id) {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        id = crypto.randomUUID().replace(/-/g, '');
      } else {
        id = `v${Date.now()}${Math.random().toString(36).slice(2, 14)}`;
      }
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return `v${Date.now()}${Math.random().toString(36).slice(2, 14)}`;
  }
}

/** Normalize phrases the same way as CreatorChatIntentService on the server. */
export function normalizeCreatorChatPhrase(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

type MatchableAction = {
  active?: boolean;
  actionSlug?: string | null;
  label?: string | null;
  synonyms?: string | null;
  videoUrl?: string | null;
};

/**
 * Instant client-side synonym match for already-uploaded clips.
 * Mirrors Java CreatorChatIntentService.keywordMatch (longest phrase wins).
 */
export function matchCreatorChatActionVideo(
  message: string,
  actions: MatchableAction[] | null | undefined
): string | null {
  const normalized = normalizeCreatorChatPhrase(message);
  if (!normalized || !actions || actions.length === 0) return null;

  let bestUrl: string | null = null;
  let bestLen = 0;

  for (const action of actions) {
    if (action.active === false) continue;
    const url = (action.videoUrl || '').trim();
    if (!url || url.includes('example.com')) continue;

    const phrases: string[] = [];
    if (action.actionSlug) {
      phrases.push(normalizeCreatorChatPhrase(action.actionSlug.replace(/_/g, ' ')));
    }
    if (action.label) {
      phrases.push(normalizeCreatorChatPhrase(action.label));
    }
    if (action.synonyms) {
      for (const part of action.synonyms.split(',')) {
        phrases.push(normalizeCreatorChatPhrase(part));
      }
    }

    for (const phrase of phrases) {
      if (!phrase) continue;
      const hit =
        normalized === phrase ||
        normalized.includes(phrase) ||
        (phrase.length >= 4 &&
          normalized.replace(/ /g, '').includes(phrase.replace(/ /g, '')));
      if (hit && phrase.length > bestLen) {
        bestLen = phrase.length;
        bestUrl = url;
      }
    }
  }

  return bestUrl;
}

const preloadedVideoCache = new Map<string, HTMLVideoElement>();

function normalizeCreatorChatVideoUrl(url: string | null | undefined): string {
  return (url || '').trim();
}

function cachedCreatorChatVideo(url: string): HTMLVideoElement | null {
  const normalized = normalizeCreatorChatVideoUrl(url);
  if (!normalized) return null;
  return preloadedVideoCache.get(normalized) || null;
}

/** True when a hidden preload element has enough buffer to start playback quickly. */
export function isCreatorChatVideoHot(url: string | null | undefined): boolean {
  const el = cachedCreatorChatVideo(url || '');
  if (!el) return false;
  // HAVE_FUTURE_DATA (3) or HAVE_ENOUGH_DATA (4)
  return el.readyState >= 3;
}

/** Initial UI state when switching to an action clip. */
export function getCreatorChatActionVideoPlayState(url: string | null | undefined): {
  ready: boolean;
  loading: boolean;
} {
  const hot = isCreatorChatVideoHot(url);
  return { ready: hot, loading: !hot };
}

/**
 * Call on pointer/touch down (dartboard wedge, etc.) to buffer the clip before click completes.
 */
export function warmCreatorChatVideoOnGesture(url: string | null | undefined): void {
  const normalized = normalizeCreatorChatVideoUrl(url);
  if (!normalized || normalized.includes('example.com')) return;
  preloadCreatorChatVideos([normalized]);
  const el = preloadedVideoCache.get(normalized);
  if (!el || el.readyState >= 3) return;
  try {
    el.currentTime = 0;
  } catch {
    /* ignore */
  }
  void el
    .play()
    .then(() => {
      if (el.readyState < 2) return;
      el.pause();
      try {
        el.currentTime = 0;
      } catch {
        /* ignore */
      }
    })
    .catch(() => undefined);
}

/** Warm the browser cache so trigger clips start without a cold download. */
export function preloadCreatorChatVideos(urls: Array<string | null | undefined>): void {
  if (typeof document === 'undefined') return;
  for (const raw of urls) {
    const url = normalizeCreatorChatVideoUrl(raw);
    if (!url || url.includes('example.com') || preloadedVideoCache.has(url)) continue;
    const v = document.createElement('video');
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', 'true');
    v.src = url;
    try {
      v.load();
    } catch {
      /* ignore */
    }
    preloadedVideoCache.set(url, v);
  }
}

/** Poll until SUCCEEDED/FAILED or timeout (~2 min). Returns video URL or null. */
export async function pollChatVideoJob(
  fetchStatus: () => Promise<PublicVideoJobStatus>,
  opts?: { intervalMs?: number; maxMs?: number; onTick?: (s: PublicVideoJobStatus) => void }
): Promise<string | null> {
  const intervalMs = opts?.intervalMs ?? 4000;
  const maxMs = opts?.maxMs ?? 120000;
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    const s = await fetchStatus();
    opts?.onTick?.(s);
    if (s.status === 'SUCCEEDED' && s.videoUrl) {
      return s.videoUrl;
    }
    if (s.status === 'FAILED') {
      return null;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function creatorChatWidgetUrl(publicId: string): string {
  if (typeof window === 'undefined') {
    return `/widget/chat/${publicId}`;
  }
  return `${window.location.origin}/widget/chat/${publicId}`;
}

export interface RetailCreatorChatCatalogItem {
  publicId: string;
  title?: string | null;
  subtitle?: string | null;
  welcomeMessage?: string | null;
  accentColor?: string | null;
  avatarImageUrl?: string | null;
  targetPlatform?: string | null;
  creatorLabel?: string | null;
  actionsEnabled?: boolean;
  voiceEnabled?: boolean;
  updatedAt?: string | null;
}

export const creatorChatRetailService = {
  listPublishedModals: async (): Promise<RetailCreatorChatCatalogItem[]> => {
    const res = await apiService.get<RetailCreatorChatCatalogItem[]>(
      `/api/retail/creator-chat/modals?_ts=${Date.now()}`
    );
    if (!res.success || !Array.isArray(res.data)) {
      throw new Error(res.error || 'Failed to load creator chat bots');
    }
    return res.data;
  },
};
